import {
	ARTICLE_FONT_BOLD_VAR,
	ARTICLE_FONT_FAMILY_VAR,
	ARTICLE_FONT_ITALIC_VAR,
	ARTICLE_JUSTIFY_VAR,
} from '@/lib/article-renderer/core/font-scale';
import {
	ANALYTICS_PREFERENCE_KEY,
	READER_STATS_PREFERENCE_KEY,
	ARTICLE_ALIGN_KEY,
	ARTICLE_FONT_KEY,
	DARK_ARTICLES_KEY,
	PLAYBACK_RATE_KEY,
	READING_MODE_KEY,
	REPLICA_LAYOUT_KEY,
	UNDERLINE_LINKS_KEY,
} from '@/constants';

import config from '@/config';

import { createPreferenceStore, setRootAttribute } from './preference-store';

export { applyStoredPreferences } from './preference-store';

/**
 * The Settings dialog's preferences that don't need React context, in one
 * module so `main.tsx` can apply every saved value at startup
 * (`applyStoredPreferences`) just by importing it. Theme, motion, text size
 * and line spacing have their own hooks (`@shared/lib/hooks`).
 */

/** Font stacks for the article font override; `publisher` removes it. */
export const ARTICLE_FONTS = {
	publisher: null,
	sans: "system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
	serif: "Charter, 'Iowan Old Style', Georgia, 'Times New Roman', serif",
} as const;
export type ArticleFont = keyof typeof ARTICLE_FONTS;

/**
 * Replaces the publisher's fonts in article text. The article CSS reads
 * every font family through `--article-font-family` (see `CSSBuilder`),
 * so setting it on `<html>` overrides them all at once.
 */
export const articleFontPreference = createPreferenceStore<ArticleFont>({
	key: ARTICLE_FONT_KEY,
	fallback: 'publisher',
	values: ['publisher', 'sans', 'serif'],
	apply: (font) => {
		const stack = ARTICLE_FONTS[font];
		const { style } = document.documentElement;
		if (stack) {
			style.setProperty(ARTICLE_FONT_FAMILY_VAR, stack);
			// Keeps the bold and italic a publisher's font files drew.
			style.setProperty(ARTICLE_FONT_BOLD_VAR, '700');
			style.setProperty(ARTICLE_FONT_ITALIC_VAR, 'italic');
			// For fonts set inside publisher HTML, which the variable can't
			// reach (see index.css).
			setRootAttribute('data-article-font', font);
		} else {
			setRootAttribute('data-article-font', null);
			style.removeProperty(ARTICLE_FONT_FAMILY_VAR);
			style.removeProperty(ARTICLE_FONT_BOLD_VAR);
			style.removeProperty(ARTICLE_FONT_ITALIC_VAR);
		}
	},
});

/**
 * `start` turns publisher-justified text ragged-right (WCAG 1.4.8: justified
 * text's uneven word gaps are hard for some readers). Only justified text
 * changes -- a centred headline stays centred -- because `CSSBuilder` emits
 * `justify` as `var(--article-justify, justify)`.
 */
export const articleAlignPreference = createPreferenceStore<'publisher' | 'start'>({
	key: ARTICLE_ALIGN_KEY,
	fallback: 'publisher',
	values: ['publisher', 'start'],
	apply: (align) => {
		if (align === 'start') {
			document.documentElement.style.setProperty(ARTICLE_JUSTIFY_VAR, 'start');
		} else {
			document.documentElement.style.removeProperty(ARTICLE_JUSTIFY_VAR);
		}
	},
});

/**
 * Darkens article pages while the app is dark. Publisher layouts carry
 * their own colours, so this inverts the page and re-inverts media (see
 * `index.css`) rather than recolouring each style.
 */
export const darkArticlesPreference = createPreferenceStore<'off' | 'on'>({
	key: DARK_ARTICLES_KEY,
	fallback: 'off',
	values: ['off', 'on'],
	apply: (value) =>
		setRootAttribute('data-dark-articles', value === 'on' ? '' : null),
});

/** Underlines every link, app and articles alike (see `index.css`). */
export const underlineLinksPreference = createPreferenceStore<'off' | 'on'>({
	key: UNDERLINE_LINKS_KEY,
	fallback: 'off',
	values: ['off', 'on'],
	apply: (value) =>
		setRootAttribute('data-underline-links', value === 'on' ? '' : null),
});

/** Audio playback speed, read by the audio player (`widgets/player`). */
export const PLAYBACK_RATES = [0.75, 1, 1.25, 1.5, 2] as const;
export type PlaybackRate = (typeof PLAYBACK_RATES)[number];
export const playbackRatePreference = createPreferenceStore<PlaybackRate>({
	key: PLAYBACK_RATE_KEY,
	fallback: 1,
	values: PLAYBACK_RATES,
});

/**
 * Whether the tenant's analytics run for this reader. Read before any
 * destination starts (`shared/analytics`); index-template.ejs reads the same key
 * to keep Google Analytics, Plausible and Statcounter from loading.
 */
export const analyticsPreference = createPreferenceStore<'on' | 'off'>({
	key: ANALYTICS_PREFERENCE_KEY,
	fallback: 'on',
	values: ['on', 'off'],
});

/**
 * Whether the app keeps the reader's own reading figures (time read,
 * articles and issues finished, streaks) on this device. Independent of
 * `analyticsPreference`: these never leave the device, so a reader who
 * shares nothing can still have them, and one who shares can switch them
 * off.
 */
export const readerStatsPreference = createPreferenceStore<'on' | 'off'>({
	key: READER_STATS_PREFERENCE_KEY,
	fallback: 'on',
	values: ['on', 'off'],
});

/**
 * The view an issue opens in, where the tenant offers both
 * (`issue.contentType: 'responsive-replica'`). The reader writes the same
 * key when the mode is switched inside an issue, so the last one used wins.
 */
export const readingModePreference = createPreferenceStore<'responsive' | 'replica'>({
	key: READING_MODE_KEY,
	fallback: 'responsive',
	values: ['responsive', 'replica'],
	external: true,
});

/** One page or two side by side, for page (replica) view. Same sharing as above. */
export const pageLayoutPreference = createPreferenceStore<'single' | 'double'>({
	key: REPLICA_LAYOUT_KEY,
	// The replica reader's own first-run default (`widgets/issue-reader/replica`).
	fallback: config?.issue?.replica?.default === 'double' ? 'double' : 'single',
	values: ['single', 'double'],
	external: true,
});
